import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import {
  X as ClearIcon,
  Sparkles as AutoAwesomeIcon,
  HelpCircle as HelpOutlineIcon,
} from 'lucide-mui';

import { FileType } from './types';
import FadeStaggerCircles from '../../../../assets/icons/fade-stagger-circles.svg';
import AIResultAccordion from './AIResultAccordion';

interface ResultsProps {
  completion: string;
  isLoading: boolean;
  stage: string;
  loadingStage: string | null;
  selectedFiles: string[];
  files: {
    data: FileType[];
  };
  activeSource: {
    fileId?: string;
    timestamp?: string;
  } | null;
  citationCount: { [key: string]: number };
  hideReferences: boolean;
  copySuccess: boolean;
  handleSourceClick: (fileId: string, timestamp: string) => void;
  handleClearSelections: () => void;
  onCopyContent: (text: string) => void;
  setCitationCount: React.Dispatch<
    React.SetStateAction<{ [key: string]: number }>
  >;
  setHideReferences: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLearnMore: React.Dispatch<React.SetStateAction<boolean>>;
}

const LOADING_MESSAGES = {
  transcripts: 'Processing transcripts...',
  ai: 'Analyzing content...',
  response: 'Generating insights...',
};

export const formatTimestamp = (seconds: string): string => {
  const totalSeconds = parseInt(seconds, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const Results: React.FC<ResultsProps> = ({
  completion,
  isLoading,
  stage,
  loadingStage,
  selectedFiles,
  files,
  activeSource,
  citationCount,
  hideReferences,
  copySuccess,
  handleSourceClick,
  handleClearSelections,
  onCopyContent,
  setCitationCount,
  setHideReferences,
  setShowLearnMore,
}) => {
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setCitationCount({});
    }
  }, [selectedFiles]);

  if (!completion && selectedFiles.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: 24,
              color: 'text.secondary',
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          Select recordings to analyze
          <Tooltip title="Learn more about Multi Ask AI" arrow>
            <IconButton
              size="small"
              onClick={() => setShowLearnMore(true)}
              sx={{ color: 'action.active' }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: '400px',
          }}
        >
          Choose one or more recordings from the sidebar to start analyzing with
          AI
        </Typography>
      </Box>
    );
  }

  if (!completion && selectedFiles.length > 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: 24,
              color: 'primary.contrastText',
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            mb: 1,
          }}
        >
          Ready to analyze
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: '400px',
          }}
        >
          Use a suggestion above or type your own question to analyze the
          selected recordings
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
      {(completion || isLoading) && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: '12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                Based on selected recordings:
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {completion && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AIResultAccordion
                    onCopyContent={onCopyContent}
                    copySuccess={copySuccess}
                    downloadText={completion}
                    fileName="ai-analysis-results.txt"
                    hideReferences={hideReferences}
                    setHideReferences={setHideReferences}
                  />
                </Box>
              )}
              <Tooltip title="Clear results" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={handleClearSelections}
                  sx={{
                    color: 'text.secondary',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    padding: '6px',
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: 'secondary.light',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              wordBreak: 'break-word',
              width: '100%',
              '& img': { maxWidth: '100%' },
              '& pre': {
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                width: '100%',
              },
              '& code': {
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              },
              '& p': {
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                width: '100%',
              },
              '& ul, & ol': {
                width: '100%',
                paddingLeft: '20px',
              },
              '& li': {
                width: '100%',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => {
                  if (href?.includes('@')) {
                    if (hideReferences) return null;
                    const [fileId, timestamp] = href.split('@');
                    const sourceKey = `${fileId}@${timestamp}`;
                    const isActive =
                      activeSource?.fileId === fileId &&
                      activeSource?.timestamp === timestamp;
                    const file = files.data.find((f) => f._id === fileId);

                    React.useEffect(() => {
                      if (!citationCount[sourceKey]) {
                        setCitationCount((prev) => {
                          const existingNumbers = Object.values(prev);
                          let nextNumber = 1;
                          while (existingNumbers.includes(nextNumber)) {
                            nextNumber += 1;
                          }
                          return {
                            ...prev,
                            [sourceKey]: nextNumber,
                          };
                        });
                      }
                    }, [sourceKey]);

                    const number = citationCount[sourceKey] || '...';

                    return (
                      <Tooltip
                        title={`${isActive ? 'Hide' : 'Show'} source: ${
                          file?.name || fileId
                        } at ${formatTimestamp(timestamp)}`}
                        arrow
                      >
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            handleSourceClick(fileId, timestamp);
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '1.25rem',
                            height: '1.25rem',
                            borderRadius: '50%',
                            backgroundColor: isActive
                              ? 'secondary.dark'
                              : 'secondary.light',
                            color: 'secondary.contrastText',
                            cursor: 'pointer',
                            fontSize: '0.625rem',
                            fontWeight: 500,
                            verticalAlign: 'baseline',
                            position: 'relative',
                            top: '-0.125em',
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            minWidth: '1.25rem',
                          }}
                        >
                          {number}
                        </Box>
                      </Tooltip>
                    );
                  }
                  return <Link href={href}>{children}</Link>;
                },
              }}
            >
              {completion}
            </ReactMarkdown>
          </Box>

          {isLoading && stage === 'processing' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 3,
                gap: 2,
              }}
            >
              {loadingStage && (
                <>
                  <img
                    src={FadeStaggerCircles}
                    alt="Loading..."
                    width={40}
                    height={40}
                    tabIndex={-1}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {
                      LOADING_MESSAGES[
                        loadingStage as keyof typeof LOADING_MESSAGES
                      ]
                    }
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Results;
